import { Controller } from '@hotwired/stimulus';
import { loadScript } from '@paypal/paypal-js';
import Rails from '@rails/ujs';
import * as Sentry from '@sentry/browser';
import { toast } from '../utils/toast';
const INPUT_ERROR_CLASS = 'input__field--error';
export default class default_1 extends Controller {
    static targets = [
        'card',
        'name',
        'nameError',
        'error',
        'form',
        'submit',
        'applePayWalletSelector',
        'googlePayWalletSelector',
        'paypalWalletSelector',
        'creditCardInputFields',
        'paypalButton',
        'loader',
        'walletPaymentAmount',
    ];
    static values = {
        paypalSetupToken: String,
        paypalIdToken: String,
        paypalClientId: String,
        stripeSetupTokenPath: String,
        cardMethodSerialized: String,
        applePayMethodSerialized: String,
        googlePayMethodSerialized: String,
        paypalMethodSerialized: String,
        stripeProcessorSerialized: String,
        paypalProcessorSerialized: String,
        currency: String,
        country: String,
    };
    submitEventListener = this.submit.bind(this);
    async connect() {
        if (!window.Stripe) {
            this.loadStripe().then(() => {
                this.initializeStripe();
            });
        }
        else {
            this.initializeStripe();
        }
        if (this.paypalSetupTokenValue && this.paypalIdTokenValue) {
            this.initializePaypal();
        }
    }
    loadStripe() {
        return new Promise((resolve, reject) => {
            const scriptElement = document.createElement('script');
            // Sanity E2E shop
            const disableAntiFraud = window.__fw_app__.shopId === 'sh_83e8a2a6-6ada-44e4-9c96-b7230e534164';
            scriptElement.src = `https://js.stripe.com/v3/${disableAntiFraud ? '?advancedFraudSignals=false' : ''}`;
            scriptElement.onload = () => resolve();
            scriptElement.onerror = () => reject();
            document.head.appendChild(scriptElement);
        });
    }
    async initializePaypal() {
        loadScript({ clientId: this.paypalClientIdValue }).then((paypal) => {
            paypal
                ?.Buttons?.({
                locale: 'en_US',
                style: {
                    color: 'gold',
                    // size: 'large',
                    label: 'pay',
                    height: 54,
                },
                // @ts-ignore
                createVaultSetupToken: () => this.paypalSetupTokenValue,
                // @ts-ignore
                onApprove: ({ vaultSetupToken }) => {
                    this.paypalButtonTarget.classList.add('disabled');
                    this.handlePaypalPaymentMethod(vaultSetupToken);
                },
                onClick: () => {
                    this.paypalButtonTarget.classList.add('disabled');
                },
                onCancel: () => {
                    this.paypalButtonTarget.classList.remove('disabled');
                },
                onError: (error) => {
                    this.paypalButtonTarget.classList.remove('disabled');
                    Sentry.captureException(error);
                },
            })
                .render(this.paypalButtonTarget)
                .then(() => {
                this.paypalWalletSelectorTarget.classList.remove('hidden');
                // The PayPal button will be rendered in an html element with the ID
                // 'paypal-button'. This function will be called when the PayPal button
                // is set up and ready to be used
            });
        });
    }
    initializeStripe() {
        const stripeKey = document.querySelector('meta[name="stripe-key"]')?.getAttribute('content');
        if (!stripeKey)
            throw new Error('Stripe key not found');
        this.stripe = Stripe(stripeKey, { locale: 'en' });
        let elements = this.stripe.elements({
            fonts: [
                {
                    cssSrc: '/styles/css-variables.scss.css',
                },
            ],
        });
        this.element.addEventListener('submit', this.submitEventListener);
        // Get CSS variables values
        const inputColor = getComputedStyle(document.documentElement).getPropertyValue('--color-on-background');
        const inputPlaceholderColor = getComputedStyle(document.documentElement).getPropertyValue('--color-on-background-rgb');
        const inputWeight = getComputedStyle(document.documentElement).getPropertyValue('--font-weight-base');
        const inputFont = getComputedStyle(document.documentElement).getPropertyValue('--font-family-base');
        // Setup regular payments
        this.card = elements.create('card', {
            hidePostalCode: false,
            hideIcon: true,
            style: {
                base: {
                    color: inputColor,
                    fontFamily: inputFont,
                    fontWeight: inputWeight,
                    fontSize: '16px',
                    fontSmoothing: 'antialiased',
                    '::placeholder': {
                        color: `rgba(${inputPlaceholderColor}, 0.5)`,
                    },
                },
            },
        });
        this.card.mount(this.cardTarget);
        this.card.addEventListener('change', this.changed.bind(this));
        this.selectedPaymentMethod = this.cardMethodSerializedValue;
        if (this.walletPaymentAmount() !== null) {
            this.walletTimeout = setTimeout(() => {
                this.submitTarget.disabled = false;
                document.querySelectorAll('.payment-method__container')[0].classList.remove('hidden');
                if (this.hasLoaderTarget) {
                    this.hideLoader();
                }
            }, 3000);
            this.initializeWallets();
        }
        else {
            this.submitTarget.disabled = false;
        }
    }
    walletPaymentAmount() {
        if (this.hasWalletPaymentAmountTarget) {
            return parseInt(this.walletPaymentAmountTarget.dataset.paymentAmount ?? '0');
        }
        else {
            return null;
        }
    }
    initializeWallets() {
        const amount = this.walletPaymentAmount();
        if (amount === null)
            return;
        this.paymentRequest = this.stripe?.paymentRequest({
            country: this.countryValue || 'US',
            currency: this.currencyValue?.toLowerCase() || 'usd',
            total: {
                label: 'Subscription',
                amount,
            },
            requestPayerName: true,
            requestPayerEmail: false,
            // disableWallets: ['browserCard'],
        });
        this.paymentRequest?.canMakePayment().then((result) => {
            clearTimeout(this.walletTimeout);
            document.querySelectorAll('.payment-method__container')[0].classList.remove('hidden');
            if (this.hasLoaderTarget) {
                this.hideLoader();
            }
            this.submitTarget.disabled = false;
            if (result) {
                this.showWalletSelector(result);
            }
        });
        this.paymentRequest?.on('paymentmethod', (event) => {
            const paymentMethod = event.paymentMethod;
            if (paymentMethod) {
                this.submitTarget.disabled = true;
                this.setupNewCard({
                    payment_method: paymentMethod.id,
                });
            }
            event.complete('success'); // Completes the transaction and closes Google/Apple pay
        });
    }
    showWalletSelector(result) {
        if (result.applePay) {
            this.applePayWalletSelectorTarget.classList.remove('hidden');
            this.googlePayWalletSelectorTarget.remove();
        }
        if (result.googlePay) {
            this.googlePayWalletSelectorTarget.classList.remove('hidden');
            this.applePayWalletSelectorTarget.remove();
        }
    }
    showPaypalButton() {
        this.submitTarget.classList.add('hidden');
        this.paypalButtonTarget.classList.remove('hidden');
    }
    hidePaypalButton() {
        this.submitTarget.classList.remove('hidden');
        this.paypalButtonTarget.classList.add('hidden');
    }
    selectPaymentMethod(event) {
        // Highlight selected method
        Array.from(document.querySelectorAll('.payment-method__container')).forEach((e) => e.classList.remove('active'));
        const target = event.target;
        if (!target)
            return;
        if (!(target instanceof HTMLInputElement))
            return;
        target.closest('.payment-method__container')?.classList.add('active');
        this.selectedPaymentMethod = target.value;
        if (this.selectedPaymentMethod === this.cardMethodSerializedValue) {
            this.creditCardInputFieldsTarget.classList.remove('hidden');
            this.hidePaypalButton();
        }
        else if (this.selectedPaymentMethod === this.paypalMethodSerializedValue) {
            this.creditCardInputFieldsTarget.classList.add('hidden');
            this.showPaypalButton();
        }
        else {
            this.creditCardInputFieldsTarget.classList.add('hidden');
            this.hidePaypalButton();
        }
    }
    changed(event) {
        if (event.error) {
            this.errorTarget.textContent = event.error.message;
        }
        else {
            this.errorTarget.textContent = '';
        }
    }
    keydown(event) {
        if (event.keyCode == 13) {
            // Catch Enter key's form submission and process as submit
            event.preventDefault();
            this.submit(event);
        }
        if (this.nameTarget.value !== '') {
            this.nameTarget.classList.remove(INPUT_ERROR_CLASS);
            this.nameErrorTarget.textContent = '';
        }
    }
    submit(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.selectedPaymentMethod === this.applePayMethodSerializedValue ||
            this.selectedPaymentMethod === this.googlePayMethodSerializedValue) {
            this.handleAsWalletPayment();
        }
        else {
            this.handleAsStripeCreditCardPayment();
        }
    }
    handleAsWalletPayment() {
        const amount = this.walletPaymentAmount();
        if (amount === null)
            return;
        // Take current amount to be paid and pass it to the payment request right before showing it
        // @ts-ignore
        this.paymentRequest?.update({
            total: {
                label: 'Subscription',
                amount,
            },
        });
        this.paymentRequest?.show();
    }
    handleAsStripeCreditCardPayment() {
        if (this.nameTarget.value == '' && this.hasNameErrorTarget) {
            this.nameTarget.classList.add(INPUT_ERROR_CLASS);
            this.nameErrorTarget.textContent = 'Name on card is required.';
            return;
        }
        this.setupNewCard({
            payment_method: {
                card: this.card,
                billing_details: {
                    name: this.nameTarget.value,
                },
            },
        });
    }
    async fetchSetupToken() {
        const formData = Object.fromEntries(new FormData(this.formTarget));
        const recaptchaResponseCheckout = formData['g-recaptcha-response-data[checkout]'];
        const recaptchaResponse = formData['g-recaptcha-response'];
        const response = await fetch(this.stripeSetupTokenPathValue, {
            method: 'POST',
            body: JSON.stringify({
                'g-recaptcha-response-data': {
                    checkout: recaptchaResponseCheckout,
                },
                'g-recaptcha-response': recaptchaResponse,
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            return null;
        }
        return await response.json();
    }
    async setupNewCard(data) {
        this.submitTarget.disabled = true;
        const setupTokens = await this.fetchSetupToken();
        if (!setupTokens) {
            return (this.submitTarget.disabled = false);
        }
        this.stripe?.confirmCardSetup(setupTokens.setupIntentClientSecret, data).then((result) => {
            if (result.error) {
                if (this.selectedPaymentMethod === this.applePayMethodSerializedValue ||
                    this.selectedPaymentMethod === this.googlePayMethodSerializedValue) {
                    toast(result?.error?.message ?? 'An error occurred');
                    if (result.error.code !== 'card_declined' && result.error.code !== 'incorrect_number') {
                        Sentry.withScope((scope) => {
                            scope.setExtra('event', result.error);
                            Sentry.captureMessage('Wallet payment method error');
                        });
                    }
                }
                this.errorTarget.textContent = result.error.message ?? 'An error occurred';
                this.submitTarget.disabled = false;
            }
            else {
                this.handleStripePaymentMethod(result.setupIntent?.payment_method ?? '');
            }
        });
    }
    handleStripePaymentMethod(payment_method_token) {
        this.handlePaymentMethod(this.stripeProcessorSerializedValue, payment_method_token);
    }
    handlePaypalPaymentMethod(payment_method_token) {
        this.handlePaymentMethod(this.paypalProcessorSerializedValue, payment_method_token);
    }
    handlePaymentMethod(processor, payment_method_token) {
        this.element.removeEventListener('submit', this.submitEventListener);
        this.addHiddenField('parameters[processor]', processor);
        this.addHiddenField('parameters[payment_method_token]', payment_method_token);
        Rails.fire(this.formTarget, 'submit');
        this.submitTarget.disabled = true;
    }
    addHiddenField(name, value) {
        let hiddenInput = document.createElement('input');
        hiddenInput.setAttribute('type', 'hidden');
        hiddenInput.setAttribute('name', name);
        hiddenInput.setAttribute('value', value);
        this.formTarget.appendChild(hiddenInput);
    }
    hideLoader() {
        this.loaderTarget.classList.add('hidden');
    }
}
